import * as Sentry from "@sentry/react";

// DSN loaded from environment variable to prevent Sentry quota abuse
// via direct spoofed error submissions to a hardcoded public DSN.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    // 10% of transactions sampled for performance monitoring
    tracesSampleRate: 0.1,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text/inputs for student privacy (TASC content, answers)
        maskAllText: true,
        blockAllMedia: false,
      }),
    ],
    // 5% of sessions captured for replay
    replaysSessionSampleRate: 0.05,
    // 100% of error sessions captured for replay
    replaysOnErrorSampleRate: 1.0,
    // Only report errors thrown by our own scripts
    allowUrls: [
      /https?:\/\/(www\.)?unlockedtutoring\.com\.au/,
      /https?:\/\/[^/]*unlockedtutoring\.lovable\.app/,
      /^https?:\/\/localhost(:\d+)?/,
    ],
    // Browser extensions and injected third-party scripts
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-(web-)?extension:\/\//i,
      /flock\.js/i,
      /^\/~/,
    ],
    // Suppress known non-actionable noise
    ignoreErrors: [
      // React dev warnings (fire via console.error, not real exceptions)
      /Warning: Function components cannot be given refs/,
      "Warning: Function components cannot be given refs",
      /Warning: Each child in a list should have a unique "key"/,
      "Warning: Each child in a list should have a unique",
      /ResizeObserver loop limit exceeded/,
      "ResizeObserver loop limit exceeded",
      /ResizeObserver loop completed with undelivered notifications/,
      "ResizeObserver loop completed with undelivered notifications",
      // Network noise — transient failures not worth alerting on
      /Failed to fetch/,
      /NetworkError/,
      /Load failed/,
      /TypeError: cancelled/,
      /AbortError/,
    ],
    beforeSend(event) {
      // Drop events that have no stack frames — these are typically
      // console.error() calls (React warnings) intercepted by Sentry,
      // not real thrown exceptions.
      const frames = event.exception?.values?.[0]?.stacktrace?.frames;
      if (event.level === 'error' && (!frames || frames.length === 0)) {
        return null;
      }

      // Drop events where no frame belongs to our own bundle — these come from
      // browser extensions or other injected third-party scripts.
      if (frames && frames.length > 0) {
        const OWN_CODE = /(unlockedtutoring\.com\.au|unlockedtutoring\.lovable\.app|localhost(:\d+)?)\//i;
        const hasOwnFrame = frames.some((f) => !!f.filename && OWN_CODE.test(f.filename));
        if (!hasOwnFrame) {
          return null;
        }
      }

      return event;
    },
  });
}

export { Sentry };
